import queryString from 'query-string';

type ApiCallContextValue = string | number | boolean | undefined;

export type ApiCallDatadogContext = Record<string, ApiCallContextValue>;

const ADS_LIBRARY_PATH = /^\/(facebook|google)_ads_library\/(eu|us)\/(.+)$/;

const AD_CREATIVES_LIST_PATH = /^\/(facebook|google)_ads_library\/(eu|us)\/ad_creatives\/?$/;
const AD_CREATIVE_DETAIL_PATH = /^\/(facebook|google)_ads_library\/(eu|us)\/ad_creatives\/([^/]+)\/?$/;

const FILTER_PARAM_KEYS = [
  'company_id',
  'branch_id',
  'page_id',
  'domain',
  'flow',
  'ad_creative_type',
  'language',
  'date_from',
  'date_to',
  'running',
  'new_only',
  'include_welltech',
  'order_by',
  'desc',
  'advertiser_id',
  'format',
  'last_shown_recently',
  'limit',
  'offset',
] as const;

const getRequestPath = (url?: string): string => {
  if (!url) {
    return '';
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      return new URL(url).pathname;
    } catch {
      return '';
    }
  }

  const path = url.split('?')[0] ?? '';
  return path.startsWith('/') ? path : `/${path}`;
};

const normalizeQueryValue = (value: unknown): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => normalizeQueryValue(item))
      .filter((item): item is string => item !== undefined);
    return normalized.length ? normalized.join(',') : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return undefined;
};

const getQueryParam = (params: unknown, key: string): string | undefined => {
  if (!params || typeof params !== 'object') {
    return undefined;
  }

  return normalizeQueryValue((params as Record<string, unknown>)[key]);
};

const getQueryParamFromUrl = (url: string | undefined, key: string): string | undefined => {
  if (!url?.includes('?')) {
    return undefined;
  }

  const query = url.split('?').slice(1).join('?');
  const parsed = queryString.parse(query);
  return normalizeQueryValue(parsed[key]);
};

const getFilterParams = (url: string | undefined, params: unknown): ApiCallDatadogContext => {
  const context: ApiCallDatadogContext = {};

  FILTER_PARAM_KEYS.forEach((key) => {
    const value = getQueryParam(params, key) ?? getQueryParamFromUrl(url, key);
    if (value !== undefined) {
      context[key] = value;
    }
  });

  return context;
};

const getAdsLibraryPathContext = (pathname: string): ApiCallDatadogContext => {
  const match = ADS_LIBRARY_PATH.exec(pathname);
  if (!match) {
    return {};
  }

  const [, network, region, resource] = match;

  return {
    network,
    region,
    adsLibraryResource: resource,
  };
};

export const getApiCallDatadogContext = (
  method: string | undefined,
  url: string | undefined,
  responseData?: unknown,
  params?: unknown,
): ApiCallDatadogContext => {
  if (!url || method?.toUpperCase() !== 'GET') {
    return {};
  }

  const pathname = getRequestPath(url);
  const adsLibraryContext = getAdsLibraryPathContext(pathname);

  if (!Object.keys(adsLibraryContext).length) {
    return {};
  }

  const creativeDetailMatch = AD_CREATIVE_DETAIL_PATH.exec(pathname);
  if (creativeDetailMatch) {
    return {
      ...adsLibraryContext,
      endpoint: 'ad_creative_detail',
      creativeId: creativeDetailMatch[3],
    };
  }

  if (AD_CREATIVES_LIST_PATH.exec(pathname)) {
    const listResponse = responseData as { meta?: { count?: number } } | undefined;

    return {
      ...adsLibraryContext,
      endpoint: 'ad_creatives_list',
      resultCount: listResponse?.meta?.count,
      ...getFilterParams(url, params),
    };
  }

  return {
    ...adsLibraryContext,
    endpoint: adsLibraryContext.adsLibraryResource,
    ...getFilterParams(url, params),
  };
};
